import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Car, PawPrint, ShieldCheck, Heart, MessageCircle, FileText, Truck, Home as HomeIcon, Quote, Star } from "lucide-react";
import api from "@/lib/api";
import RouteMarker from "@/components/RouteMarker";
import SarapeDivider from "@/components/SarapeDivider";
import Seo from "@/components/Seo";
import FaqSection from "@/components/FaqSection";
import ReviewsBlock from "@/components/ReviewsBlock";
import { ChecklistCapture } from "@/components/ChecklistCapture";
import { useLang } from "@/context/LanguageContext";
import { useClientAuth } from "@/context/ClientAuthContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const T = {
  en: {
    heroTitle: "Your move to Mexico, made calm and clear.",
    heroSubtitle: "We help English-speaking US residents plan, coordinate, and feel confident about relocating to Mexico — one step at a time.",
    petsBadge: "We move pets",
    petsBadgeNote: "— vet paperwork, travel crates, the works",
    getQuote: "Get a quote",
    bookCall: "Book an intro call",
    refundNote: "If the call doesn't help, we'll refund it — no questions asked.",
    exploringSuffix: "people exploring their move with us this month",
    whatWeDo: "What we do",
    whatWeDoSub: "Three services, one calm move — pick what you need or combine them all.",
    services: [
      { icon: Compass, title: "Relocation Guidance", text: "Residency paperwork, timelines, budgets, and a written roadmap — in plain English.", to: "/how-we-help", cta: "How we help" },
      { icon: Car, title: "Driving & Transport", text: "Door-to-door drivers and border crossing support, from your US doorstep to Mexico.", to: "/drivers-transport", cta: "Drivers & transport" },
      { icon: PawPrint, title: "Pet Transport", text: "Vet paperwork, travel crates, and calm pet-friendly routes. No quarantine.", to: "/pet-transport", cta: "Pet transport" },
    ],
    allServices: "See all services & guides",
    howItWorks: "How it works",
    howItWorksSub: 'Four steps from "thinking about it" to "home in Mexico."',
    steps: [
      { icon: MessageCircle, title: "Tell us about your move", text: "Take the quick quiz or get an instant estimate — no sign-up needed." },
      { icon: FileText, title: "Get your quote & plan", text: "A written quote and step-by-step roadmap, tailored to your route and pets." },
      { icon: Truck, title: "We coordinate & drive", text: "Paperwork, border crossing, and door-to-door transport — handled for you." },
      { icon: HomeIcon, title: "Settle in supported", text: "Arrival help, local tips, and ongoing support from our Mérida home base." },
    ],
    startQuiz: "Start the quiz",
    instantEstimate: "Get an instant estimate",
    trust: [
      { icon: ShieldCheck, title: "Guidance, not guesswork", text: "Clear, honest answers about residency, driving, pets, and logistics." },
      { icon: Heart, title: "Calm and low-pressure", text: "No hard sells. Just structure, reassurance, and a plan that fits your life." },
      { icon: Car, title: "Every path covered", text: "Driving, hired transport, pets, non-Spanish speakers — we've got you." },
    ],
    meridaMarker: "Based in Mérida, Yucatán",
    meridaTitle: "Mérida is our home base.",
    meridaText: "One of Mexico's safest, warmest, and most welcoming cities — and the place we know best. Considering somewhere else? We'll help you compare, no pressure either way.",
    discoverMerida: "Discover Mérida",
    exploreOther: "Explore other places",
    valuesLabel: "Our values",
    valuesTitle: "You are safe with us.",
    valuesIntro: "Everyone deserves to feel safe, respected, and supported during one of the biggest transitions of their life. We are proudly inclusive and LGBTQ+ friendly.",
    valuesMore: "Read our full commitment",
    valuesP1: "Whether you are a same-sex couple, a transgender individual, a non-binary person, or an ally, you are welcome here. We do not ask you to hide who you are, and we will never make you feel like you have to. You will be treated with the same warmth, professionalism, and care as every other client we serve.",
    valuesP2: "We also believe that human worth is not defined by immigration status. Every person who comes to us for help deserves dignity and humane treatment, full stop. We do not judge. We do not discriminate. We help.",
    valuesP3: "Our commitment to your safety goes beyond paperwork. We choose the safest routes, even when they take longer. We look out for our clients, their loved ones, and their pets. When you move with Hola Movers, you are not just a transaction — you are a person we are honored to serve.",
    allFaqs: "See all FAQs",
    questionTitle: "Just have a question?",
    questionText: "Send us a friendly message — no pressure, no payment. We read every one.",
    freeContact: "Free contact form",
    planTitle: "Ready for a real plan?",
    planText: "Book a 45-minute intro consultation. We'll map your whole move together and send a written roadmap.",
    bookIntro: "Book intro call",
  },
  es: {
    heroTitle: "Tu mudanza a México, con calma y claridad.",
    heroSubtitle: "Ayudamos a residentes de EE. UU. a planear, coordinar y sentirse seguros con su mudanza a México — un paso a la vez.",
    petsBadge: "Mudamos mascotas",
    petsBadgeNote: "— papeleo veterinario, transportadoras, todo incluido",
    getQuote: "Cotiza ahora",
    bookCall: "Agenda una llamada",
    refundNote: "Si la llamada no te ayuda, te la reembolsamos — sin preguntas.",
    exploringSuffix: "personas explorando su mudanza con nosotros este mes",
    whatWeDo: "Lo que hacemos",
    whatWeDoSub: "Tres servicios, una mudanza tranquila — elige lo que necesites o combínalos.",
    services: [
      { icon: Compass, title: "Orientación para tu mudanza", text: "Trámites de residencia, tiempos, presupuestos y una hoja de ruta por escrito — claro y sencillo.", to: "/how-we-help", cta: "Cómo ayudamos" },
      { icon: Car, title: "Manejo y transporte", text: "Choferes puerta a puerta y apoyo en el cruce fronterizo, desde tu casa en EE. UU. hasta México.", to: "/drivers-transport", cta: "Choferes y transporte" },
      { icon: PawPrint, title: "Transporte de mascotas", text: "Papeleo veterinario, transportadoras y rutas tranquilas para mascotas. Sin cuarentena.", to: "/pet-transport", cta: "Transporte de mascotas" },
    ],
    allServices: "Ver todos los servicios y guías",
    howItWorks: "Cómo funciona",
    howItWorksSub: 'Cuatro pasos de "lo estoy pensando" a "en casa en México."',
    steps: [
      { icon: MessageCircle, title: "Cuéntanos de tu mudanza", text: "Responde el quiz o pide un estimado al instante — sin registrarte." },
      { icon: FileText, title: "Recibe tu cotización y plan", text: "Una cotización por escrito y una hoja de ruta paso a paso, a tu medida." },
      { icon: Truck, title: "Coordinamos y manejamos", text: "Papeleo, cruce fronterizo y transporte puerta a puerta — nos encargamos." },
      { icon: HomeIcon, title: "Te instalas con apoyo", text: "Ayuda al llegar, consejos locales y apoyo continuo desde Mérida." },
    ],
    startQuiz: "Empieza el quiz",
    instantEstimate: "Obtén un estimado al instante",
    trust: [
      { icon: ShieldCheck, title: "Orientación, no adivinanzas", text: "Respuestas claras y honestas sobre residencia, manejo, mascotas y logística." },
      { icon: Heart, title: "Con calma y sin presión", text: "Sin ventas agresivas. Solo estructura, tranquilidad y un plan a tu medida." },
      { icon: Car, title: "Todos los caminos cubiertos", text: "Manejando, con chofer, con mascotas o sin hablar español — te cubrimos." },
    ],
    meridaMarker: "Con base en Mérida, Yucatán",
    meridaTitle: "Mérida es nuestra base.",
    meridaText: "Una de las ciudades más seguras, cálidas y acogedoras de México — y el lugar que mejor conocemos. ¿Consideras otro destino? Te ayudamos a comparar, sin presión.",
    discoverMerida: "Descubre Mérida",
    exploreOther: "Explora otros lugares",
    valuesLabel: "Nuestros valores",
    valuesTitle: "Estás a salvo con nosotros.",
    valuesIntro: "Todos merecen sentirse seguros, respetados y apoyados durante una de las transiciones más grandes de su vida. Somos orgullosamente inclusivos y amigables con la comunidad LGBTQ+.",
    valuesMore: "Lee nuestro compromiso completo",
    valuesP1: "Seas una pareja del mismo sexo, una persona transgénero, no binaria o un aliado, aquí eres bienvenido. No te pedimos que ocultes quién eres, y nunca te haremos sentir que debes hacerlo. Recibirás la misma calidez, profesionalismo y cuidado que cada cliente que atendemos.",
    valuesP2: "También creemos que el valor humano no se define por el estatus migratorio. Cada persona que acude a nosotros merece dignidad y un trato humano, punto. No juzgamos. No discriminamos. Ayudamos.",
    valuesP3: "Nuestro compromiso con tu seguridad va más allá del papeleo. Elegimos las rutas más seguras, aunque tomen más tiempo. Cuidamos a nuestros clientes, a sus seres queridos y a sus mascotas. Cuando te mudas con Hola Movers, no eres una transacción — eres una persona a la que nos honra servir.",
    allFaqs: "Ver todas las preguntas",
    questionTitle: "¿Solo tienes una pregunta?",
    questionText: "Envíanos un mensaje amistoso — sin presión, sin pago. Leemos cada uno.",
    freeContact: "Formulario de contacto gratis",
    planTitle: "¿Listo para un plan de verdad?",
    planText: "Agenda una consulta de 45 minutos. Trazaremos toda tu mudanza juntos y te enviaremos una hoja de ruta por escrito.",
    bookIntro: "Agenda tu llamada",
  },
};

export default function Home() {
  const { lang, t } = useLang();
  const { client } = useClientAuth();
  const s = T[lang] || T.en;
  const [hero, setHero] = useState(null);
  const [introPrice, setIntroPrice] = useState(null);
  const [exploring, setExploring] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [nicoCard, setNicoCard] = useState(null);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const id = hash.slice(1);
      [0, 400, 900, 1500].forEach((d) =>
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: d === 0 ? "auto" : "smooth" }), d)
      );
    } else {
      window.scrollTo(0, 0);
    }
    api.get("/content/page/home").then((res) => setHero(res.data));
    api.get("/review-media/homepage?review_key=nico").then((res) => setNicoCard(res.data)).catch(() => {});
    api.get("/stats/exploring").then((res) => setExploring(res.data.exploring_this_month || 0)).catch(() => {});
    api.get("/content/merida-profile").then((res) => setTestimonials((res.data.testimonials || []).slice(0, 3))).catch(() => {});
    api.get("/appointment-types").then((res) => {
      const paid = res.data.find((t) => t.deposit_amount > 0);
      setIntroPrice(paid ? paid.deposit_amount : 0);
    }).catch(() => {});
  }, []);
  // Re-align hash target after the biggest async layout shifts (hero image/content).
  useEffect(() => {
    const hash = window.location.hash;
    if (hero && hash && hash.length > 1) {
      setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [hero]);
  const introLabel = introPrice > 0 ? ` · $${introPrice}` : "";
  const tr = (en, es) => (lang === "es" && es ? es : en);

  const heroImage = hero?.hero_image || "";

  return (
    <div data-testid="page-home">
      <Seo path="/" image={heroImage || undefined}
        description="Hola Movers — calm, clear guidance for English-speaking US residents moving to Mexico, with Mérida, Yucatán as our home base."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Hola Movers relocation services",
          serviceType: "Relocation service",
          description: "Planning, coordination, door-to-door driving and pet transport for English-speaking US residents moving to Mexico.",
          areaServed: ["United States", "Mexico"],
          provider: { "@type": "Organization", name: "Hola Movers" },
          offers: [{ "@type": "Offer", name: "Intro consultation", price: String(introPrice || 75), priceCurrency: "USD" }],
        }} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {heroImage && <img src={heroImage} alt="A sunlit colonial street in Mérida, Mexico — the kind of calm new home Hola Movers helps US residents move to" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-sand-100 via-sand-100/90 to-sand-100/20" />
        </div>
        <div className="absolute inset-0 pattern-talavera opacity-[0.05]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-24 lg:py-36">
          <div className="max-w-2xl animate-fade-up">
            <RouteMarker className="mb-5" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.04]">
              {tr(hero?.hero_title, hero?.hero_title_es) || s.heroTitle}
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-charcoal-600 leading-relaxed max-w-xl">
              {tr(hero?.hero_subtitle, hero?.hero_subtitle_es) || s.heroSubtitle}
            </p>
            <Link
              to="/pets"
              data-testid="hero-pets-badge"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-sand-300 px-4 py-2 text-sm font-semibold text-charcoal-700 hover:text-terracotta hover:border-terracotta/40 transition-colors shadow-sm"
            >
              {s.petsBadge} <span aria-hidden="true">🐾</span>
              <span className="font-normal text-charcoal-500 hidden sm:inline">{s.petsBadgeNote}</span>
            </Link>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link to="/cost-calculator" data-testid="hero-quote-cta" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-3.5 text-white font-medium hover:bg-terracotta-hover transition-colors shadow-sm">
                {s.getQuote}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/book" data-testid="hero-book-cta" className="rounded-full border border-charcoal-300 px-8 py-3.5 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
                {s.bookCall}{introLabel}
              </Link>
            </div>
            <p data-testid="hero-trust-note" className="mt-4 text-sm text-charcoal-500">
              {s.refundNote}
            </p>
            {exploring >= 5 && (
              <p data-testid="hero-exploring-count" className="mt-2 text-sm text-charcoal-500">
                <span className="font-semibold text-charcoal-700">{exploring.toLocaleString()}</span> {s.exploringSuffix}
              </p>
            )}
          </div>
        </div>
        <SarapeDivider className="relative" />
      </section>

      {/* 3 service cards */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pt-16 lg:pt-24" data-testid="home-services">
        <div className="max-w-2xl mb-10">
          <h2 className="text-3xl sm:text-4xl font-normal text-charcoal-800">{s.whatWeDo}</h2>
          <p className="mt-3 text-charcoal-600 text-lg">{s.whatWeDoSub}</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {s.services.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`home-service-${c.to.slice(1)}`}
              className="group flex flex-col rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] transition-all hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(44,42,41,0.1)]"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta-light text-terracotta mb-6">
                <c.icon strokeWidth={1.5} />
              </span>
              <h3 className="text-xl font-medium text-charcoal-800">{c.title}</h3>
              <p className="mt-2 flex-1 text-charcoal-600 leading-relaxed">{c.text}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-terracotta">
                {c.cta}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/services" data-testid="home-all-services" className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors">
            {s.allServices} <ArrowRight size={16} />
          </Link>
        </div>

        {/* Meet Nico — near the pets services */}
        <div data-testid="home-meet-nico" className="mt-10 rounded-3xl border border-sand-200 bg-sage-light p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          {nicoCard && nicoCard.url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${nicoCard.url}`}
              alt={lang === "es" ? nicoCard.alt_es : nicoCard.alt_en}
              loading="lazy"
              data-testid="home-meet-nico-photo"
              className="h-20 w-20 shrink-0 rounded-2xl object-cover border border-sand-200"
            />
          ) : (
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-terracotta">
              <PawPrint strokeWidth={1.5} />
            </span>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-medium text-charcoal-800">{t("home.meetNicoTitle")}</h3>
            <p className="mt-2 text-charcoal-600 leading-relaxed">{t("home.meetNicoText")}</p>
          </div>
          <Link
            to="/nicos-story"
            data-testid="home-meet-nico-link"
            className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors"
          >
            {t("home.readNicoStory")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* How it works — 4 steps */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24" data-testid="home-steps">
        <div className="rounded-3xl bg-sand-200 p-8 lg:p-14">
          <div className="max-w-2xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-normal text-charcoal-800">{s.howItWorks}</h2>
            <p className="mt-3 text-charcoal-600 text-lg">{s.howItWorksSub}</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {s.steps.map((st, i) => (
              <div key={st.title} className="relative">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-terracotta mb-4">
                  <st.icon strokeWidth={1.5} size={20} />
                </span>
                <span className="absolute top-0 right-0 font-serif text-4xl text-sand-400/60">{i + 1}</span>
                <h3 className="text-lg font-medium text-charcoal-800">{st.title}</h3>
                <p className="mt-1.5 text-sm text-charcoal-600 leading-relaxed">{st.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/start-here" data-testid="home-steps-cta" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors shadow-sm">
              {s.startQuiz}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/cost-calculator" data-testid="home-steps-quote" className="rounded-full border border-charcoal-300 px-7 py-3 text-charcoal-800 font-medium hover:bg-white transition-colors">
              {s.instantEstimate}
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-sage-light" data-testid="home-trust">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-3">
            {s.trust.map((r) => (
              <div key={r.title} className="flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-sage-700">
                  <r.icon strokeWidth={1.5} size={20} />
                </span>
                <div>
                  <h3 className="text-lg font-medium text-charcoal-800">{r.title}</h3>
                  <p className="mt-1 text-charcoal-600 leading-relaxed">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews block (replaces the old testimonials section) */}
      <ReviewsBlock />

      {/* Mérida home base */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-3xl border border-sand-300">
            <img
              src="https://images.unsplash.com/photo-1695385246289-2e7d2ab5e16b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Colorful colonial street in Mérida, Yucatán"
              className="h-[300px] w-full object-cover"
            />
          </div>
          <div>
            <RouteMarker className="mb-5" label={s.meridaMarker} />
            <h2 className="text-3xl sm:text-4xl font-normal text-charcoal-800">{s.meridaTitle}</h2>
            <p className="mt-4 text-lg text-charcoal-600 leading-relaxed">{s.meridaText}</p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link to="/merida" data-testid="home-merida-cta" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors shadow-sm">
                {s.discoverMerida}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/explore-mexico" data-testid="home-explore-cta" className="rounded-full border border-charcoal-300 px-7 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
                {s.exploreOther}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our values — condensed into accordion */}
      <section className="bg-night" data-testid="home-values">
        <div className="mx-auto max-w-4xl px-6 lg:px-12 py-16 lg:py-20">
          <div className="flex items-center gap-2 text-clay">
            <Heart size={18} strokeWidth={1.5} />
            <span className="text-sm uppercase tracking-[0.2em]">{s.valuesLabel}</span>
          </div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-sand-100 leading-tight">
            {s.valuesTitle}
          </h2>
          <p className="mt-4 text-sand-100/80 leading-relaxed">{s.valuesIntro}</p>
          <Accordion type="single" collapsible className="mt-6">
            <AccordionItem value="values" className="border-white/10">
              <AccordionTrigger data-testid="home-values-trigger" className="text-sand-100 hover:text-terracotta hover:no-underline text-base font-medium">
                {s.valuesMore}
              </AccordionTrigger>
              <AccordionContent className="space-y-5 text-sand-100/80 leading-relaxed text-base">
                <p>{s.valuesP1}</p>
                <p>{s.valuesP2}</p>
                <p>{s.valuesP3}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* FAQ teaser */}
      <FaqSection page="home" />
      <div className="mx-auto max-w-3xl px-6 lg:px-12 -mt-8 pb-8">
        <Link to="/faq" data-testid="home-faq-link" className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors">
          {s.allFaqs} <ArrowRight size={16} />
        </Link>
      </div>

      {/* Client testimonial — Nico's family */}
      <section id="testimonials" data-testid="home-testimonials" className="scroll-mt-24 mx-auto max-w-4xl px-6 lg:px-12 py-12 lg:py-16">
        <h2 className="text-3xl sm:text-4xl font-normal text-charcoal-800 text-center">{t("home.testimonialsTitle")}</h2>
        <figure className="mt-8 rounded-3xl bg-white border border-sand-200 p-6 sm:p-10 shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
          <Quote className="text-terracotta" size={28} strokeWidth={1.5} />
          <span className="mt-4 flex text-terracotta" role="img" aria-label={t("home.fiveStarsAria")} data-testid="owner-review-stars">
            {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={18} fill="currentColor" strokeWidth={1.5} />)}
          </span>
          <h3 className="mt-2 font-serif text-2xl text-charcoal-800">{t("home.ownerReviewTitle")}</h3>
          <blockquote className="mt-4 space-y-4 text-charcoal-700 leading-relaxed">
            {t("home.ownerReviewBody").split("\n\n").map((p, i) => (
              <p key={i} data-testid={`testimonial-p-${i}`}>{p}</p>
            ))}
          </blockquote>
          <figcaption className="mt-5 text-sm font-medium text-charcoal-800">— {t("home.ownerReviewAttribution")}</figcaption>
        </figure>
        <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
          <Link
            to="/nicos-story"
            data-testid="testimonials-read-nico"
            className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors"
          >
            {t("home.readNicoStory")} <ArrowRight size={16} />
          </Link>
          <Link
            to={client ? "/portal" : "/account"}
            data-testid="testimonials-client-signin"
            className="inline-flex items-center gap-2 rounded-full border border-charcoal-300 px-7 py-3 font-medium text-charcoal-800 hover:bg-sand-200 transition-colors"
          >
            {client ? t("common.myPortal") : t("common.clientSignIn")}
          </Link>
        </div>
      </section>

      {/* Two paths CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-12 lg:py-16">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl bg-white p-10 border border-sand-200">
            <h3 className="font-serif text-2xl text-charcoal-800">{s.questionTitle}</h3>
            <p className="mt-3 text-charcoal-600 leading-relaxed">{s.questionText}</p>
            <Link to="/contact" data-testid="home-free-contact" className="mt-6 inline-flex rounded-full border border-charcoal-300 px-7 py-3 font-medium text-charcoal-800 hover:bg-sand-200 transition-colors">
              {s.freeContact}
            </Link>
          </div>
          <div className="rounded-3xl bg-night p-10 text-sand-100">
            <h3 className="font-serif text-2xl">{s.planTitle}</h3>
            <p className="mt-3 text-sand-100/80 leading-relaxed">{s.planText}</p>
            <Link to="/book" data-testid="home-paid-book" className="mt-6 inline-flex rounded-full bg-terracotta px-7 py-3 font-medium text-white hover:bg-terracotta-hover transition-colors">
              {s.bookIntro}{introLabel}
            </Link>
          </div>
        </div>
      </section>

      <ChecklistCapture />
    </div>
  );
}
